/* Default shipping protection follows the actual cart and preserves opt-outs. */
(() => {
  if (window.orydawnShippingProtection) return;
  const preferenceKey = 'orydawn_shipping_protection';
  let pendingChoice;
  let running = false;
  let queued = false;
  let timer;
  let manualUpdate = false;
  const root = () => window.Shopify?.routes?.root || '/';
  const control = () => document.querySelector('.gb-shipping-protection-button label.switch');
  const variantId = () => document.querySelector('.shipping-protection-form input[name="id"]')?.value;
  function paint(cart, id) {
    const label = control();
    if (!label) return;
    const checked = cart.items.some(item => String(item.variant_id) === id);
    label.classList.toggle('checked', checked);
    label.classList.toggle('unchecked', !checked);
    const input = label.querySelector('input');
    if (input) {
      input.checked = checked;
      input.disabled = running && manualUpdate;
      input.setAttribute('aria-label', 'Shipping Protection');
    }
    label.setAttribute('aria-busy', String(running && manualUpdate));
    const spinner = label.querySelector('.spin-animation');
    if (spinner) spinner.style.display = running && manualUpdate ? 'block' : 'none';
  }
  async function json(url, options) {
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update Shipping Protection.');
    return data;
  }
  // A native form POST keeps this automatic fee adjustment separate from the
  // storefront's AJAX product-add instrumentation. No pixel hooks are changed.
  function updateProtection(body) {
    return new Promise((resolve, reject) => {
      const frame = document.createElement('iframe');
      const form = document.createElement('form');
      let settled = false;
      frame.name = 'orydawn-protection-' + Date.now();
      frame.src = 'about:blank';
      frame.hidden = true;
      frame.setAttribute('aria-hidden', 'true');
      form.hidden = true;
      form.method = 'post';
      form.action = root() + 'cart/update.js';
      form.target = frame.name;
      function field(name, value) {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        form.append(input);
      }
      for (const [id, quantity] of Object.entries(body.updates)) field(`updates[${id}]`, quantity);
      for (const [key, value] of Object.entries(body.attributes || {})) field(`attributes[${key}]`, value);
      field('sections', body.sections.join(','));
      field('sections_url', body.sections_url);
      function finish(error, cart) {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        frame.remove(); form.remove();
        if (error) reject(error); else resolve(cart);
      }
      const timeout = setTimeout(() => finish(new Error('Shipping Protection update timed out.')), 20000);
      frame.addEventListener('load', () => {
        if (settled) return;
        frame.addEventListener('load', async () => {
          if (settled) return;
          try {
            // Browsers can give JSON navigation documents an opaque origin.
            // Verify the saved cart instead of reading the frame document.
            const cart = await json(root() + 'cart.js', { cache: 'no-store' });
            for (const [id, quantity] of Object.entries(body.updates)) {
              const actual = cart.items.filter(item => item.key === id || String(item.variant_id) === id)
                .reduce((total, item) => total + item.quantity, 0);
              if (actual !== Number(quantity)) throw new Error('Shipping Protection quantity was not saved.');
            }
            for (const [key, value] of Object.entries(body.attributes || {})) {
              if (cart.attributes[key] !== value) throw new Error('Shipping Protection preference was not saved.');
            }
            const url = new URL(body.sections_url, location.origin);
            url.searchParams.set('sections', body.sections.join(','));
            cart.sections = await json(url.href, { cache: 'no-store' });
            finish(null, cart);
          } catch (error) { finish(error); }
        }, { once: true });
        form.submit();
      }, { once: true });
      document.body.append(frame, form);
    });
  }
  async function refresh(cart) {
    const sections = cart.sections;
    const html = new DOMParser();
    if (sections?.['cart-drawer']) {
      const current = document.querySelector('#CartDrawer');
      const next = html.parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
      if (current && next && typeof updateCartDrawer === 'function') updateCartDrawer(current, next, false);
    }
    if (sections?.['cart-icon-bubble']) {
      const icon = document.getElementById('cart-icon-bubble');
      const next = html.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (icon && next) icon.innerHTML = next.innerHTML;
    }
    for (const name of ['main-cart-items', 'main-cart-footer']) {
      const current = document.getElementById(name);
      if (!current) continue;
      const key = current.dataset.id || name;
      if (!sections?.[key]) continue;
      const next = html.parseFromString(sections[key], 'text/html').querySelector('.js-contents');
      const target = current.querySelector('.js-contents');
      if (next && target) {
        target.innerHTML = next.innerHTML;
        const container = name === 'main-cart-items' ? current.closest('cart-items') : current;
        if (container) container.classList.toggle('is-empty', cart.item_count === 0);
      }
    }
  }
  async function reconcile() {
    if (running) { queued = true; return; }
    const id = variantId();
    if (!id || !control()) return;
    running = true;
    const explicitChoice = pendingChoice;
    pendingChoice = undefined;
    manualUpdate = explicitChoice !== undefined;
    let cart;
    try {
      cart = await json(root() + 'cart.js', { cache: 'no-store' });
      if (manualUpdate) paint(cart, id);
      const enabled = explicitChoice === undefined ? cart.attributes[preferenceKey] !== 'off' : explicitChoice;
      const hasProducts = cart.items.some(item => String(item.variant_id) !== id);
      const lines = cart.items.filter(item => String(item.variant_id) === id);
      const quantity = lines.reduce((n, item) => n + item.quantity, 0);
      const desired = hasProducts && enabled ? 1 : 0;
      if (quantity !== desired || explicitChoice !== undefined) {
        const updates = {};
        if (lines.length) {
          lines.forEach((item, index) => { updates[item.key] = index === 0 ? desired : 0; });
        } else if (desired) updates[id] = 1;
        const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
        for (const name of ['main-cart-items', 'main-cart-footer']) {
          const element = document.getElementById(name);
          if (element) sectionIds.push(element.dataset.id || name);
        }
        const body = { updates, sections: sectionIds, sections_url: location.pathname };
        if (explicitChoice !== undefined) body.attributes = { [preferenceKey]: enabled ? 'on' : 'off' };
        cart = await updateProtection(body);
        await refresh(cart);
      }
      const error = document.getElementById('orydawn-protection-error');
      if (error) error.remove();
    } catch (error) {
      console.error('Shipping Protection update failed:', error);
      const label = control();
      if (label && !document.getElementById('orydawn-protection-error')) {
        const message = document.createElement('p');
        message.id = 'orydawn-protection-error';
        message.setAttribute('role', 'alert');
        message.textContent = 'Shipping Protection could not be updated. Please try again.';
        label.closest('.gb-shipping-protection').append(message);
      }
    } finally {
      running = false;
      manualUpdate = false;
      if (cart) paint(cart, id);
      if (queued || pendingChoice !== undefined) { queued = false; schedule(); }
    }
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(reconcile, 150); }
  // Keep product add payloads/responses untouched for theme and pixel consumers.
  // Protection uses a native cart/update.js POST, never another cart/add.
  window.orydawnShippingProtection = { sync: reconcile };
  document.addEventListener('click', event => {
    const label = event.target.closest('.gb-shipping-protection-button label.switch');
    if (!label) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pendingChoice = !label.classList.contains('checked');
    schedule();
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches('.gb-shipping-protection-button input')) return;
    event.stopImmediatePropagation();
    pendingChoice = event.target.checked;
    schedule();
  }, true);
  function start() {
    schedule();
    for (const name of ['cart-drawer:open', 'cart-drawer:update', 'cart-drawer:render', 'cart:ajax-update']) document.addEventListener(name, schedule);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/**
 * * end of cart upsell block part
 */
document.addEventListener("DOMContentLoaded", function () {
  const amountFreeProduct = document.querySelector("[data-treshold-product]")
    ? document.querySelector("[data-treshold-product]").getAttribute("data-treshold-product")
    : null;

  var cartTotalEl = document.querySelector(".gb-totals-total-value");
  var cart_total = cartTotalEl ? (cartTotalEl.textContent || "").trim() : "";
  if (amountFreeProduct > 0 && show_progress_bar) {
    const hasPremiumKit = document.querySelector(".premium-attachment-kit") !== null;
    if (hasPremiumKit) {
      console.log("Premium kit detected - skipping auto free gift trigger");
      return;
    }
    setTimeout(function () {
      var el = document.querySelector(".gb-totals-total-value");
      var cart_total = el ? (el.textContent || "").trim() : "";
      if (cart_total >= amountFreeProduct) {
        if (document.querySelector(".cart-item.gb-find-remove-product")) {
          console.log("Free product already in cart");
        } else {
          console.log("Adding free product");
          var trigger = document.querySelector(".gb-free-product-trigger");
          if (trigger) trigger.click();
        }
      } else {
        console.log("Removing free product - under threshold");
        var removeBtn = document.querySelector(".gb-remove-product");
        if (removeBtn) removeBtn.click();
      }
    }, 3000);

    document.body.addEventListener("click", function (e) {
      if (!e.target.closest(".gb-sumbit-free")) return;
      setTimeout(function () {
        var el = document.querySelector(".gb-totals-total-value");
        var cart_total = el ? (el.textContent || "").trim() : "";
        if (cart_total >= amountFreeProduct) {
          if (!document.querySelector(".cart-item.gb-find-remove-product")) {
            var trigger = document.querySelector(".gb-free-product-trigger");
            if (trigger) trigger.click();
          }
        } else {
          var removeBtn = document.querySelector(".gb-remove-product");
          if (removeBtn) removeBtn.click();
        }
      }, 3000);
    });
  }

  document.body.addEventListener("click", async function (e) {
    if (window.orydawnShippingProtection) return;
    var label = e.target.closest && e.target.closest(".gb-shipping-protection-button label.switch");
    if (!label) return;
    if (e.target.tagName === "INPUT") return;
    const curElem = document.querySelector(".gb-shipping-protection-button label.switch");
    const curInputElem = document.querySelector(".gb-shipping-protection-button label.switch input");
    const spinAnim = document.querySelector(".gb-shipping-protection-button .spin-animation");
    const checkElem = document.querySelector(".gb-shipping-protection-button .complete-check");
    const wasChecked = curElem && curElem.classList.contains("checked");

    if (!wasChecked) {
      if (!curElem || !curInputElem || !spinAnim || !checkElem) return;
      if (spinAnim.style.display === "block" || curInputElem.checked) return;
      const form = document.querySelector("form.shipping-protection-form");

      const formData = new FormData(form);
      if (!formData.get("id")) {
        console.error(
          "There is no shipping protection product selected. Please select the shipping product in the global theme settings",
        );
        return;
      }

      spinAnim.style.display = "block";

      try {
        const response = await fetch(routes.cart_add_url, {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (response.ok) {
          spinAnim.style.display = "none";

          checkElem.style.display = "block";
        }
      } catch (err) {
        console.error("error at shipping protection form submission", err);
      }
      fetch(routes.cart_url + "?view=drawer")
        .then((response) => response.text())
        .then((html) => {
          const cartDrawer = document.querySelector("#CartDrawer");
          const parsedCartHtml = parser.parseFromString(html, "text/html");
          const newCartDrawer = parsedCartHtml.querySelector("#CartDrawer");
          if (cartDrawer && newCartDrawer) {
            updateCartDrawer(cartDrawer, newCartDrawer, true, () => {
              curElem.classList.remove("unchecked");
              curElem.classList.add("checked");
              curInputElem.checked = true;
            });
          }
        })
        .catch((_err) => {
          curElem.classList.remove("unchecked");
          curElem.classList.add("checked");
          curInputElem.checked = true;
        });
    } else {
      checkElem.style.display = "none";
      const cartDrawer = document.querySelector("#CartDrawer");
      const removeButton = cartDrawer.querySelector(`cart-remove-button[data-id="${shippingProductId}"]`);
      const shippingItemIndex = removeButton?.dataset.index;

      const removeCallback = () => {
        curElem.classList.remove("checked");
        curElem.classList.add("unchecked");
        curInputElem.checked = false;
      };

      const cartItems = cartDrawer.querySelector("cart-items") || cartDrawer.querySelector("cart-drawer-items");

      if (removeButton && shippingItemIndex && cartItems?.updateQuantity) {
        const event = new CustomEvent("remove-shipping-protection", {
          detail: null,
        });
        cartItems.updateQuantity(shippingItemIndex, 0, event, "", "", removeCallback);
      } else {
        fetch(routes.cart_change_url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ id: shippingProductId, quantity: 0 }),
        })
          .then(() => fetch(routes.cart_url + "?view=drawer"))
          .then((response) => response.text())
          .then((html) => {
            const cartDrawer = document.querySelector("#CartDrawer");
            const parsedCartHtml = parser.parseFromString(html, "text/html");
            const newCartDrawer = parsedCartHtml.querySelector("#CartDrawer");
            if (cartDrawer && newCartDrawer) {
              updateCartDrawer(cartDrawer, newCartDrawer, true, removeCallback);
            } else {
              removeCallback();
            }
          })
          .catch(() => {
            removeCallback();
          });
      }
    }
  });

  document.body.addEventListener("change", function (e) {
    var select = e.target.closest && e.target.closest(".gb-change-variant_id");
    if (!select) return;
    var opt = select.options[select.selectedIndex];
    var id_change = opt ? opt.getAttribute("data-variant-id") : null;
    if (!id_change) {
      console.error("No data-variant-id found on selected option");
      return;
    }
    var block = select.closest(".gb-get-main-freq-pro");
    var input = block ? block.querySelector(".product-variant-id") : null;
    if (input) input.value = id_change;
  });
});

document.addEventListener("change", function (e) {
  if (e.target.matches(".cart-drawer .gbfrequently-bought-with-main-whole .select__select")) {
    const parentBlock = e.target.closest(".gb-get-main-freq-pro");
    if (!parentBlock) {
      console.log("Error: Could not find parent block");
      return;
    }

    const allValues = Array.from(parentBlock.querySelectorAll(".select__select"))
      .map((sel) => sel.value)
      .join(" / ");

    const variantSelect = parentBlock.querySelector('[name="variants"]');
    if (!variantSelect) {
      console.log("Error: Could not find variant selector");
      return;
    }

    const matchedOption = Array.from(variantSelect.options).find((option) => option.text.includes(allValues));

    const idInput = parentBlock.querySelector('[name="id"]');
    if (!idInput) {
      console.log("Error: Could not find id input field");
      return;
    }

    if (matchedOption) {
      console.log("Matched variant option value:", matchedOption.value);
      idInput.value = matchedOption.value;
      console.log("Set ID input value to:", idInput.value);
    } else {
      console.log("No matching variant found for:", allValues);
    }
  }
});

// Add validation for form submissions: require id when in single-variant mode; skip when form has items[] (bundles / premium kit)
document.addEventListener("submit", function (e) {
  if (typeof Elixir_ProductFormExists !== 'function' || !Elixir_ProductFormExists()) return;
  if (typeof Elixir_GetProductForm !== 'function' || Elixir_GetProductForm() !== e.target) return;
  if (typeof Elixir_GetProductFormVariantId !== 'function') return;
  if (typeof Elixir_ProductFormHasItemsInputs === 'function' && Elixir_ProductFormHasItemsInputs()) return;
  const variantId = Elixir_GetProductFormVariantId();
  if (!variantId) {
    console.log("Preventing submission - missing id parameter (single-product form has no variant id)");
    e.preventDefault();
    if (window.ThemeEditorToast && typeof window.ThemeEditorToast.show === 'function') {
      window.ThemeEditorToast.show('Add to cart failed: missing variant ID on product form.', { type: 'error', duration: 5000 });
    }
    return false;
  }
});

document.addEventListener("DOMContentLoaded", function () {
  function handleCartQuantityChange(e) {
    var cartItem = e.target.closest(".cart-item");
    if (!cartItem) return;
    var trigger =
      e.target.closest(".quantity__button") ||
      e.target.closest(".quantity__input") ||
      (e.target.matches && e.target.matches(".quantity__input") ? e.target : null);
    if (!trigger) return;
    var quantityInputWrap = trigger.closest(".quantity-input");
    var qtyInput = quantityInputWrap ? quantityInputWrap.querySelector(".quantity__input") : null;
    var variantId = qtyInput ? qtyInput.getAttribute("data-quantity-variant-id") : null;
    var form = trigger.closest("form");
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput && variantId) {
      console.log("Adding missing ID input to cart form");
      var newIdInput = document.createElement("input");
      newIdInput.type = "hidden";
      newIdInput.name = "id";
      newIdInput.value = variantId;
      form.appendChild(newIdInput);
    } else if (idInput && variantId) {
      idInput.value = variantId;
    }
  }
  document.addEventListener("click", handleCartQuantityChange);
  document.addEventListener("change", handleCartQuantityChange);
  document.addEventListener("input", handleCartQuantityChange);

  var checkoutBtn = document.getElementById("CartDrawer-Checkout");
  if (checkoutBtn) {
    checkoutBtn.addEventListener("click", function (e) {
      var form = document.getElementById("CartDrawer-Form");
      if (!form) return;
      var cartItems = form.querySelectorAll(".cart-item");
      if (cartItems.length === 0) {
        console.log("No items in cart, preventing checkout");
        e.preventDefault();
        return false;
      }
      console.log("Cart checkout with", cartItems.length, "items");
    });
  }

  document.addEventListener("keydown", function (e) {
    var input =
      (e.target.matches && e.target.matches(".quantity__input")
        ? e.target
        : e.target.closest && e.target.closest(".quantity__input")) || null;
    if (!input) return;
    if (e.key === "Enter" || e.keyCode === 13) {
      e.preventDefault();
      input.blur();
    }
  });
});

document.addEventListener("DOMContentLoaded", function () {
  document.body.addEventListener("click", function (e) {
    var btn =
      e.target.closest(".gbfrequently-bought-with-main-whole button") ||
      e.target.closest(".gbfrequently-bought-with-main-whole .gb-sumbit-free") ||
      e.target.closest(".gb-free-product-trigger") ||
      e.target.closest(".gb-product-shipping-protection-product-tirgger") ||
      e.target.closest(".gb-product-sticky-add-to-cart-form");
    if (!btn) return;
    var parentBlock = btn.closest(".gb-get-main-freq-pro") || btn.closest("form");
    if (!parentBlock) {
      console.error("Could not find parent block for frequently bought product");
      return;
    }
    var productId = null;
    var idInput = parentBlock.querySelector('input[name="id"]');
    if (idInput && idInput.value) {
      productId = idInput.value;
    } else if (parentBlock.querySelector(".gb-change-variant_id")) {
      var select = parentBlock.querySelector(".gb-change-variant_id");
      var opt = select && select.options[select.selectedIndex];
      if (opt) {
        productId = opt.getAttribute("data-variant-id");
      }
    } else if (parentBlock.querySelector(".product-variant-id")) {
      var variantInput = parentBlock.querySelector(".product-variant-id");
      productId = variantInput ? variantInput.value : null;
    } else if (btn.getAttribute("data-variant-id")) {
      productId = btn.getAttribute("data-variant-id");
    }
    console.log("Frequently bought product add to cart - ID:", productId);
    if (btn.classList.contains("gb-free-product-trigger") && !show_progress_bar) return;
    if (!productId) {
      console.error("Missing product ID for frequently bought product");
      e.preventDefault();
      return false;
    }
    if (!idInput) {
      console.log("Adding missing ID input to frequently bought product form");
      var newIdInput = document.createElement("input");
      newIdInput.type = "hidden";
      newIdInput.name = "id";
      newIdInput.value = productId;
      parentBlock.appendChild(newIdInput);
    } else {
      idInput.value = productId;
    }
    var quantityInput = parentBlock.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      console.log("Adding default quantity input to frequently bought product form");
      var newQtyInput = document.createElement("input");
      newQtyInput.type = "hidden";
      newQtyInput.name = "quantity";
      newQtyInput.value = "1";
      parentBlock.appendChild(newQtyInput);
    } else if (!quantityInput.value || parseFloat(quantityInput.value, 10) < 1) {
      quantityInput.value = "1";
    }
  });
});

// Kaching showing Price(Compare Price) Text in ATC fix

const KachingApp = () => {
  const isProductPage = document.querySelector('.shopify-section.shop-product-section');
  if (!isProductPage) return;

  let buttonObserver = null;

  const updateAddToCartButton = (bundle) => {
    const addToCartButton = document.querySelector('.shop-add-to-cart-button');
    if (!addToCartButton || !bundle) return;

    const buttonTextEl = addToCartButton.querySelector('.button-text');
    if (!buttonTextEl) return;

    const bundlePriceEl = bundle.querySelector('.kaching-bundles__bar-price');
    const bundleComparePriceEl = bundle.querySelector('.kaching-bundles__bar-full-price');

    if (bundlePriceEl) {
      const price = bundlePriceEl.textContent.trim();
      const comparePrice = bundleComparePriceEl ? bundleComparePriceEl.textContent.trim() : '';

      // Get the persistent text (e.g., "Buy Now")
      const persistentText = addToCartButton.getAttribute('data-persistent-text') || 'Buy Now';

      // Update button text with actual values
      if (comparePrice) {
        buttonTextEl.innerHTML = `${persistentText} - ${price}<span class="compare-price">(${comparePrice})</span>`;
      } else {
        buttonTextEl.innerHTML = `${persistentText} - ${price}`;
      }

      // Store current bundle reference for observer
      addToCartButton.dataset.currentBundlePrice = price;
      addToCartButton.dataset.currentBundleComparePrice = comparePrice || '';
    }
  };

  const forceUpdateButton = () => {
    const addToCartButton = document.querySelector('.shop-add-to-cart-button');
    const buttonTextEl = addToCartButton?.querySelector('.button-text');
    
    if (!buttonTextEl) return;

    const price = addToCartButton.dataset.currentBundlePrice;
    const comparePrice = addToCartButton.dataset.currentBundleComparePrice;
    const persistentText = addToCartButton.getAttribute('data-persistent-text') || 'Buy Now';

    // Check if button shows placeholders
    if (buttonTextEl.textContent.includes('[price]') || buttonTextEl.textContent.includes('[comparePrice]')) {
      if (price) {
        if (comparePrice) {
          buttonTextEl.innerHTML = `${persistentText} - ${price}<span class="compare-price">(${comparePrice})</span>`;
        } else {
          buttonTextEl.innerHTML = `${persistentText} - ${price}`;
        }
      }
    }
  };

  const setupButtonObserver = () => {
    const addToCartButton = document.querySelector('.shop-add-to-cart-button');
    if (!addToCartButton) return;

    // Disconnect existing observer if any
    if (buttonObserver) {
      buttonObserver.disconnect();
    }

    // Create new observer to watch for button text changes
    buttonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Use setTimeout to let the other script finish, then override
          setTimeout(forceUpdateButton, 0);
        }
      });
    });

    // Observe the button and its children for any changes
    buttonObserver.observe(addToCartButton, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true
    });
  };

  const updateAllPrices = (bundle) => {
    // Offer totals belong on the cart button; the Product Details price stays fixed.
    updateAddToCartButton(bundle);
    
    // Force update again after a short delay to override any competing scripts
    setTimeout(() => forceUpdateButton(), 50);
    setTimeout(() => forceUpdateButton(), 200);
  };

  const initApp = (app) => {
    const applyFirstCheckedBundle = () => {
      const selectedBundle = app.querySelector('.kaching-bundles__bar:has(input:checked)');
      if (selectedBundle) {
        updateAllPrices(selectedBundle);
      }
    };

    const initAppFunctions = () => {
      const interval = setInterval(() => {
        const selectedBundle = app.querySelector('.kaching-bundles__bar:has(input:checked)');
        if (selectedBundle) {
          clearInterval(interval);
          updateAllPrices(selectedBundle);
          setupButtonObserver();
        }
      }, 300);
    };

    app.addEventListener('change', () => {
      const selectedBundle = app.querySelector('.kaching-bundles__bar:has(input:checked)');
      updateAllPrices(selectedBundle);
    });

    applyFirstCheckedBundle();
    initAppFunctions();
  };

  const interval = setInterval(() => {
    const app = document.querySelector('kaching-bundle');
    if (app) {
      clearInterval(interval);
      initApp(app);
    }
  }, 300);
};

window.addEventListener('DOMContentLoaded', KachingApp);
